"use client";

import { useCallback, useEffect, useState } from "react";
import {
	fetchTokenInfo as fetchTokenInfoAPI,
	TokenInfo as TokenInfoAPI,
} from "@/lib/utils/token-api";

interface TokenInfo {
	id: string;
	name: string;
	description: string;
	image: string;
	creator: string;
	created_time: string;
	volume: number;
	bonded: boolean;
	icrc_ledger: string;
	price: number;
	marketcap: number;
	rune: string;
	featured: boolean;
	holder_count: number;
	holder_top: number;
	holder_dev: number;
	comment_count: number;
	sold: number;
	twitter: string;
	website: string;
	telegram: string;
	last_comment_time: string;
	sell_count: number;
	buy_count: number;
	ticker: string;
	btc_liquidity: number;
	token_liquidity: number;
	user_btc_liquidity: number;
	user_token_liquidity: number;
	user_lp_tokens: number;
	total_supply: number;
	swap_fees: number;
	swap_fees_24: number;
	swap_volume: number;
	swap_volume_24: number;
	threshold: number;
	txn_count: number;
	divisibility: number;
	decimals: number;
	withdrawals: boolean;
	deposits: boolean;
	trading: boolean;
	external: boolean;
	price_5m: number;
	price_1h: number;
	price_6h: number;
	price_1d: number;
	rune_id: string;
	last_action_time: string;
	twitter_verified: boolean;
	verified: boolean;
	liquidity_threshold: number;
	progress: number;
}

interface UseTokenInfoResult {
	tokenInfo: TokenInfo | null;
	isLoading: boolean;
	error: string | null;
}

// 全局缓存
const tokenCache = new Map<string, { data: TokenInfo; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

// 防抖延迟
const DEBOUNCE_DELAY = 300; // 300ms延迟

export function useTokenInfo(tokenId: string | null): UseTokenInfoResult {
	const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchTokenInfo = useCallback(async (id: string) => {
		try {
			// 检查缓存
			const cached = tokenCache.get(id);
			const now = Date.now();

			if (cached && now - cached.timestamp < CACHE_DURATION) {
				setTokenInfo(cached.data);
				setIsLoading(false);
				setError(null);
				return;
			}

			setIsLoading(true);
			setError(null);

			const data = (await fetchTokenInfoAPI(id)) as TokenInfo;

			// 更新缓存
			tokenCache.set(id, { data, timestamp: now });

			setTokenInfo(data);
			setError(null);
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Failed to fetch token info";
			setError(errorMessage);
			setTokenInfo(null);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!tokenId) {
			setTokenInfo(null);
			setIsLoading(false);
			setError(null);
			return;
		}

		// 防抖延迟加载
		const timer = setTimeout(() => {
			fetchTokenInfo(tokenId);
		}, DEBOUNCE_DELAY);

		return () => clearTimeout(timer);
	}, [tokenId, fetchTokenInfo]);

	return {
		tokenInfo,
		isLoading,
		error,
	};
}
